import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { ActivityIcon, WifiIcon, AlertTriangleIcon, ZapIcon, MapPinIcon, UserIcon, ClockIcon, GaugeIcon, ChevronLeftIcon, ChevronRightIcon, SearchIcon } from 'lucide-react';
import { useMavisData, formatZAR } from '../hooks/useMavisData';
// Helper to assign coordinates based on address since they aren't in the raw data
const getCoordinates = (address: string): [number, number] => {
  if (address.includes('Vilakazi')) return [-26.3438, 27.8616]; // Soweto
  if (address.includes('Tembisa')) return [-25.996, 28.2268]; // Tembisa
  if (address.includes('Khayelitsha')) return [-34.0388, 18.6747]; // Khayelitsha
  if (address.includes('Protea Glen')) return [-26.31, 27.82]; // Soweto 2
  if (address.includes('Mamelodi')) return [-25.72, 28.395]; // Mamelodi
  if (address.includes('Umlazi')) return [-29.97, 30.89]; // Umlazi
  return [-28.5, 25.5]; // Default center
};
const getStatusColor = (status: string) => {
  switch (status) {
    case 'Online':
      return '#8DB43E';
    // mavis-green
    case 'Warning':
      return '#E07020';
    // mavis-orange
    case 'Offline':
      return '#ef4444';
    // red
    default:
      return '#9ca3af';
    // gray
  }
};
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'Online':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'Warning':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'Offline':
      return 'bg-red-50 text-red-600 border-red-100';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};
const getLpuTypeColor = (type: string) => {
  switch (type) {
    case 'Mall':
      return '#3b82f6';
    // blue
    case 'Factory':
      return '#8b5cf6';
    // purple
    case 'School':
      return '#22c55e';
    // green
    default:
      return '#9ca3af';
    // gray
  }
};
const getLpuTypeBadgeClass = (type: string) => {
  switch (type) {
    case 'Mall':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Factory':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'School':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};
const lpuMarkers = [{
  id: 'LPU-001',
  name: 'Eastgate Shopping Centre',
  type: 'Mall',
  location: 'Bedfordview',
  lat: -26.1076,
  lng: 28.1228,
  consumption: 185400,
  peakDemand: 4.2,
  status: 'Online',
  lastComm: '1 min ago',
  monthlyBill: 463500
}, {
  id: 'LPU-002',
  name: 'Toyota SA Manufacturing',
  type: 'Factory',
  location: 'Prospecton, Durban',
  lat: -29.9614,
  lng: 30.9541,
  consumption: 342000,
  peakDemand: 8.5,
  status: 'Online',
  lastComm: '3 mins ago',
  monthlyBill: 855000
}, {
  id: 'LPU-003',
  name: 'Menlyn Park Shopping Centre',
  type: 'Mall',
  location: 'Pretoria',
  lat: -25.7823,
  lng: 28.2756,
  consumption: 210500,
  peakDemand: 5.1,
  status: 'Warning',
  lastComm: '45 mins ago',
  monthlyBill: 526250
}, {
  id: 'LPU-004',
  name: 'Sappi Ngodwana Mill',
  type: 'Factory',
  location: 'Mpumalanga',
  lat: -25.5833,
  lng: 30.6333,
  consumption: 520000,
  peakDemand: 12.8,
  status: 'Online',
  lastComm: '2 mins ago',
  monthlyBill: 1300000
}, {
  id: 'LPU-005',
  name: 'University of Pretoria',
  type: 'School',
  location: 'Hatfield, Pretoria',
  lat: -25.7545,
  lng: 28.2314,
  consumption: 98500,
  peakDemand: 2.4,
  status: 'Online',
  lastComm: '5 mins ago',
  monthlyBill: 246250
}, {
  id: 'LPU-006',
  name: 'Sandton City Complex',
  type: 'Mall',
  location: 'Sandton',
  lat: -26.1076,
  lng: 28.0567,
  consumption: 275000,
  peakDemand: 6.8,
  status: 'Online',
  lastComm: '1 min ago',
  monthlyBill: 687500
}, {
  id: 'LPU-007',
  name: 'ArcelorMittal Vanderbijlpark',
  type: 'Factory',
  location: 'Vanderbijlpark',
  lat: -26.7117,
  lng: 27.8381,
  consumption: 890000,
  peakDemand: 22.5,
  status: 'Offline',
  lastComm: '6 hours ago',
  monthlyBill: 2225000
}, {
  id: 'LPU-008',
  name: 'Wits University',
  type: 'School',
  location: 'Braamfontein, JHB',
  lat: -26.1929,
  lng: 28.0305,
  consumption: 112000,
  peakDemand: 2.8,
  status: 'Online',
  lastComm: '8 mins ago',
  monthlyBill: 280000
}];

// Base locations for generating mock residential meters
const SA_LOCATIONS = [{
  name: 'Soweto',
  lat: -26.2485,
  lng: 27.854
}, {
  name: 'Alexandra',
  lat: -26.1076,
  lng: 28.0997
}, {
  name: 'Sandton',
  lat: -26.1076,
  lng: 28.0567
}, {
  name: 'Randburg',
  lat: -26.0936,
  lng: 28.0067
}, {
  name: 'Roodepoort',
  lat: -26.1625,
  lng: 27.8725
}, {
  name: 'Hillbrow',
  lat: -26.1917,
  lng: 28.0486
}, {
  name: 'Mamelodi',
  lat: -25.72,
  lng: 28.395
}, {
  name: 'Centurion',
  lat: -25.8603,
  lng: 28.1894
}, {
  name: 'Soshanguve',
  lat: -25.4833,
  lng: 28.0833
}, {
  name: 'Atteridgeville',
  lat: -25.7667,
  lng: 28.0833
}, {
  name: 'Hatfield',
  lat: -25.7479,
  lng: 28.2293
}, {
  name: 'Tembisa',
  lat: -25.996,
  lng: 28.2268
}, {
  name: 'Benoni',
  lat: -26.1883,
  lng: 28.3206
}, {
  name: 'Boksburg',
  lat: -26.2125,
  lng: 28.2625
}, {
  name: 'Germiston',
  lat: -26.2167,
  lng: 28.1667
}, {
  name: 'Khayelitsha',
  lat: -34.0388,
  lng: 18.6747
}, {
  name: 'Mitchells Plain',
  lat: -34.0489,
  lng: 18.6181
}, {
  name: 'Gugulethu',
  lat: -33.9781,
  lng: 18.5656
}, {
  name: 'Langa',
  lat: -33.9447,
  lng: 18.5253
}, {
  name: 'Nyanga',
  lat: -33.9867,
  lng: 18.5756
}, {
  name: 'Umlazi',
  lat: -29.97,
  lng: 30.89
}, {
  name: 'KwaMashu',
  lat: -29.745,
  lng: 30.975
}, {
  name: 'Pinetown',
  lat: -29.8167,
  lng: 30.8667
}, {
  name: 'Chatsworth',
  lat: -29.9167,
  lng: 30.8833
}, {
  name: 'Bloemfontein',
  lat: -29.1211,
  lng: 26.214
}, {
  name: 'Polokwane',
  lat: -23.9045,
  lng: 29.4689
}, {
  name: 'Nelspruit',
  lat: -25.4753,
  lng: 30.9694
}, {
  name: 'Rustenburg',
  lat: -25.6667,
  lng: 27.2417
}, {
  name: 'Kimberley',
  lat: -28.7282,
  lng: 24.7499
}, {
  name: 'Port Elizabeth',
  lat: -33.9608,
  lng: 25.6022
}, {
  name: 'East London',
  lat: -33.0153,
  lng: 27.9116
}];
const FIRST_NAMES = ['Nomsa', 'Bongani', 'Fatima', 'Pieter', 'Lindiwe', 'David', 'Precious', 'Andile', 'Sarah', 'Themba', 'Grace', 'Mandla', 'Ayanda', 'Willem', 'Nompilo', 'Sipho', 'Zanele', 'Johan', 'Lerato', 'Amina'];
const LAST_NAMES = ['Mthembu', 'Zulu', 'Ismail', 'Botha', 'Nkosi', 'Mokoena', 'Mahlangu', 'Sithole', 'Venter', 'Dube', 'Molefe', 'Shabalala', 'Cele', 'Pretorius', 'Zwane', 'Ndlovu', 'Patel', 'van der Merwe', 'Dlamini', 'Khumalo'];
const STREET_NAMES = ['Main Rd', 'Church St', 'Vilakazi St', 'Nelson Mandela Dr', 'Voortrekker Rd', 'Protea Ave', 'Section 3', 'Mxenge Rd', 'Umlazi V', 'Kruger St'];
const LAST_COMMS = ['1 min ago', '2 mins ago', '5 mins ago', '10 mins ago', '30 mins ago', '1 hour ago', '3 hours ago', '1 day ago', '2 days ago'];

// Generate 100 mock residential meters
const generatedMeters = Array.from({
  length: 100
}, () => {
  const loc = SA_LOCATIONS[Math.floor(Math.random() * SA_LOCATIONS.length)];
  const firstName = FIRST_NAMES[Math.floor(Math.random() * FIRST_NAMES.length)];
  const lastName = LAST_NAMES[Math.floor(Math.random() * LAST_NAMES.length)];
  const street = STREET_NAMES[Math.floor(Math.random() * STREET_NAMES.length)];
  const streetNum = Math.floor(Math.random() * 200) + 1;
  const rand = Math.random();
  const status = rand < 0.7 ? 'Online' : rand < 0.85 ? 'Warning' : 'Offline';
  const history = Array.from({
    length: 7
  }, () => +(Math.random() * 30).toFixed(1));
  if (status === 'Offline') {
    history[6] = 0;
    if (Math.random() > 0.5) history[5] = 0;
  }
  return {
    id: `04-${Math.floor(1000 + Math.random() * 9000)}-${Math.floor(1000 + Math.random() * 9000)}-${Math.floor(10 + Math.random() * 90)}`,
    name: `${firstName} ${lastName}`,
    address: `${streetNum} ${street}, ${loc.name}`,
    lat: loc.lat + (Math.random() - 0.5) * 0.04,
    lng: loc.lng + (Math.random() - 0.5) * 0.04,
    reading: +(Math.random() * 4900 + 100).toFixed(1),
    daily: +(Math.random() * 28 + 2).toFixed(1),
    status,
    lastComm: LAST_COMMS[Math.floor(Math.random() * LAST_COMMS.length)],
    history,
    type: 'Residential' as const
  };
});
export function MeterMap() {
  const data = useMavisData();
  const daysOfWeek = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const [viewMode, setViewMode] = useState<'all' | 'residential' | 'lpu'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMeterId, setSelectedMeterId] = useState<string | null>(null);
  const itemsPerPage = 10;
  // Combine original 6 meters with 100 generated ones
  const allResidentialMeters = useMemo(() => {
    const originalWithCoords = data.smartMeters.map(m => {
      const [lat, lng] = getCoordinates(m.address);
      return {
        ...m,
        lat,
        lng,
        type: 'Residential' as const
      };
    });
    return [...originalWithCoords, ...generatedMeters];
  }, [data.smartMeters]);
  // Get dataset based on view mode (for stats and map)
  const currentDataset = useMemo(() => {
    const lpuWithType = lpuMarkers.map(m => ({
      ...m,
      isLpu: true
    }));
    if (viewMode === 'all') return [...allResidentialMeters, ...lpuWithType];
    if (viewMode === 'residential') return allResidentialMeters;
    return lpuWithType;
  }, [viewMode, allResidentialMeters]);
  // Filter dataset based on search (for list panel)
  const filteredDataset = useMemo(() => {
    if (!searchQuery.trim()) return currentDataset;
    const query = searchQuery.toLowerCase();
    return currentDataset.filter(m => m.id.toLowerCase().includes(query) || m.name.toLowerCase().includes(query) || (m.isLpu ? m.location : m.address).toLowerCase().includes(query));
  }, [currentDataset, searchQuery]);
  // Pagination logic
  const totalPages = Math.ceil(filteredDataset.length / itemsPerPage);
  const paginatedDataset = filteredDataset.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset page when search or view mode changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, viewMode]);
  // Calculate stats based on currentDataset (ignores search filter)
  const totalMeters = currentDataset.length;
  const onlineCount = currentDataset.filter(m => m.status === 'Online').length;
  const onlineRate = totalMeters > 0 ? onlineCount / totalMeters * 100 : 0;
  const activeAlerts = currentDataset.filter(m => m.status === 'Warning' || m.status === 'Offline').length;
  let avgDaily = 0;
  let avgDailyLabel = 'Avg Daily';
  let avgDailyUnit = 'kWh';
  if (totalMeters > 0) {
    if (viewMode === 'lpu') {
      avgDaily = currentDataset.reduce((sum, m) => sum + (m.consumption || 0), 0) / totalMeters;
      avgDailyLabel = 'Avg Monthly';
    } else if (viewMode === 'residential') {
      avgDaily = currentDataset.reduce((sum, m) => sum + (m.daily || 0), 0) / totalMeters;
    } else {
      // Mixed mode - just show residential average to avoid skewing
      const resMeters = currentDataset.filter(m => !m.isLpu);
      avgDaily = resMeters.length > 0 ? resMeters.reduce((sum, m) => sum + (m.daily || 0), 0) / resMeters.length : 0;
    }
  }
  return <div className="relative h-full w-full bg-gray-50 flex" data-id="element-1442">
      {/* Map Container */}
      <div className="flex-1 relative h-full" data-id="element-1443">
        <MapContainer center={[-28.5, 25.5]} zoom={6} className="h-full w-full z-0" zoomControl={false} data-id="element-1444">

          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1445" />


          {/* Markers */}
          {currentDataset.map(meter => {
          const isSelected = selectedMeterId === meter.id;
          if (meter.isLpu) {
            const borderColor = getStatusColor(meter.status);
            const fillColor = getLpuTypeColor(meter.type);
            return <CircleMarker key={meter.id} center={[meter.lat, meter.lng]} radius={isSelected ? 18 : 14} pathOptions={{
              color: isSelected ? '#0D4F5C' : borderColor,
              weight: isSelected ? 4 : 3,
              fillColor: fillColor,
              fillOpacity: 0.9
            }} eventHandlers={{
              click: () => setSelectedMeterId(meter.id)
            }} data-id="element-1446">

                  <Tooltip direction="top" offset={[0, -14]} opacity={1} data-id="element-1447">
                    <div className="flex items-start gap-2" data-id="element-1448">
                      <div className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${meter.status === 'Online' ? 'bg-[#8DB43E]' : meter.status === 'Warning' ? 'bg-[#E07020]' : 'bg-[#ef4444]'}`} data-id="element-1449" />

                      <div data-id="element-1450">
                        <div className="font-mono font-bold text-gray-900" data-id="element-1451">
                          {meter.id}
                        </div>
                        <div className="text-gray-600" data-id="element-1452">{meter.name}</div>
                        <div className={`text-[10px] font-semibold mt-0.5 ${meter.status === 'Online' ? 'text-[#8DB43E]' : meter.status === 'Warning' ? 'text-[#E07020]' : 'text-[#ef4444]'}`} data-id="element-1453">

                          {meter.status} · {meter.type}
                        </div>
                      </div>
                    </div>
                  </Tooltip>

                  <Popup maxWidth={320} className="rounded-none" data-id="element-1454">
                    <div className="p-1 min-w-[280px]" data-id="element-1455">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-100" data-id="element-1456">
                        <div data-id="element-1457">
                          <div className="flex items-center gap-2 mb-1" data-id="element-1458">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-none text-[10px] font-medium border ${getLpuTypeBadgeClass(meter.type)}`} data-id="element-1459">

                              {meter.type}
                            </span>
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-none text-[10px] font-medium border ${getStatusBadgeClass(meter.status)}`} data-id="element-1460">

                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${meter.status === 'Online' ? 'bg-[#8DB43E]' : meter.status === 'Warning' ? 'bg-[#E07020]' : 'bg-[#ef4444]'}`} data-id="element-1461" />

                              {meter.status}
                            </span>
                          </div>
                          <h3 className="font-bold text-lg text-gray-900 m-0 leading-tight" data-id="element-1462">
                            {meter.name}
                          </h3>
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1" data-id="element-1463">
                            <ClockIcon className="w-3 h-3" data-id="element-1464" /> {meter.lastComm}
                          </div>
                        </div>
                      </div>

                      {/* Customer Info */}
                      <div className="space-y-2 mb-4" data-id="element-1465">
                        <div className="flex items-start gap-2 text-sm" data-id="element-1466">
                          <UserIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" data-id="element-1467" />
                          <span className="font-mono font-medium text-gray-900" data-id="element-1468">
                            {meter.id}
                          </span>
                        </div>
                        <div className="flex items-start gap-2 text-sm" data-id="element-1469">
                          <MapPinIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" data-id="element-1470" />
                          <span className="text-gray-600" data-id="element-1471">
                            {meter.location}
                          </span>
                        </div>
                      </div>

                      {/* Readings */}
                      <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 border border-gray-100 rounded-none" data-id="element-1472">
                        <div data-id="element-1473">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-1474">
                            Monthly Consumption
                          </p>
                          <p className="font-mono font-bold text-mavis-teal text-lg leading-none" data-id="element-1475">
                            {meter.consumption?.toLocaleString()}
                            <span className="text-xs font-sans text-gray-500 font-normal ml-1" data-id="element-1476">
                              kWh
                            </span>
                          </p>
                        </div>
                        <div data-id="element-1477">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-1478">
                            Peak Demand
                          </p>
                          <p className="font-mono font-bold text-gray-900 text-lg leading-none" data-id="element-1479">
                            {meter.peakDemand?.toFixed(1)}
                            <span className="text-xs font-sans text-gray-500 font-normal ml-1" data-id="element-1480">
                              MVA
                            </span>
                          </p>
                        </div>
                        <div className="col-span-2 pt-2 mt-1 border-t border-gray-200" data-id="element-1481">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-1482">
                            Estimated Bill
                          </p>
                          <p className="font-mono font-bold text-gray-900 text-lg leading-none" data-id="element-1483">
                            {formatZAR(meter.monthlyBill || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>;
          } else {
            const color = getStatusColor(meter.status);
            const maxHistory = Math.max(...(meter.history || [0]));
            return <CircleMarker key={meter.id} center={[meter.lat, meter.lng]} radius={isSelected ? 14 : 10} pathOptions={{
              color: isSelected ? '#0D4F5C' : '#ffffff',
              weight: isSelected ? 3 : 2,
              fillColor: color,
              fillOpacity: isSelected ? 1 : 0.8
            }} eventHandlers={{
              click: () => setSelectedMeterId(meter.id)
            }} data-id="element-1484">

                  <Tooltip direction="top" offset={[0, -10]} opacity={1} data-id="element-1485">
                    <div className="flex items-start gap-2" data-id="element-1486">
                      <div className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${meter.status === 'Online' ? 'bg-[#8DB43E]' : meter.status === 'Warning' ? 'bg-[#E07020]' : 'bg-[#ef4444]'}`} data-id="element-1487" />

                      <div data-id="element-1488">
                        <div className="font-mono font-bold text-gray-900" data-id="element-1489">
                          {meter.id}
                        </div>
                        <div className="text-gray-600" data-id="element-1490">{meter.name}</div>
                        <div className={`text-[10px] font-semibold mt-0.5 ${meter.status === 'Online' ? 'text-[#8DB43E]' : meter.status === 'Warning' ? 'text-[#E07020]' : 'text-[#ef4444]'}`} data-id="element-1491">

                          {meter.status}
                        </div>
                      </div>
                    </div>
                  </Tooltip>

                  <Popup maxWidth={320} className="rounded-none" data-id="element-1492">
                    <div className="p-1 min-w-[280px]" data-id="element-1493">
                      {/* Header */}
                      <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-100" data-id="element-1494">
                        <div data-id="element-1495">
                          <h3 className="font-mono font-bold text-lg text-gray-900 m-0 leading-tight" data-id="element-1496">
                            {meter.id}
                          </h3>
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1" data-id="element-1497">
                            <ClockIcon className="w-3 h-3" data-id="element-1498" /> {meter.lastComm}
                          </div>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-none text-xs font-medium border ${getStatusBadgeClass(meter.status)}`} data-id="element-1499">

                          <span className={`w-2 h-2 rounded-full shrink-0 ${meter.status === 'Online' ? 'bg-[#8DB43E]' : meter.status === 'Warning' ? 'bg-[#E07020]' : 'bg-[#ef4444]'}`} data-id="element-1500" />

                          {meter.status}
                        </span>
                      </div>

                      {/* Customer Info */}
                      <div className="space-y-2 mb-4" data-id="element-1501">
                        <div className="flex items-start gap-2 text-sm" data-id="element-1502">
                          <UserIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" data-id="element-1503" />
                          <span className="font-medium text-gray-900" data-id="element-1504">
                            {meter.name}
                          </span>
                        </div>
                        <div className="flex items-start gap-2 text-sm" data-id="element-1505">
                          <MapPinIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" data-id="element-1506" />
                          <span className="text-gray-600" data-id="element-1507">{meter.address}</span>
                        </div>
                      </div>

                      {/* Readings */}
                      <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 border border-gray-100 rounded-none" data-id="element-1508">
                        <div data-id="element-1509">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-1510">
                            Total Reading
                          </p>
                          <p className="font-mono font-bold text-mavis-teal text-lg leading-none" data-id="element-1511">
                            {meter.reading?.toFixed(1)}
                            <span className="text-xs font-sans text-gray-500 font-normal ml-1" data-id="element-1512">
                              kWh
                            </span>
                          </p>
                        </div>
                        <div data-id="element-1513">
                          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-1514">
                            Today's Usage
                          </p>
                          <p className="font-mono font-bold text-gray-900 text-lg leading-none" data-id="element-1515">
                            {meter.daily?.toFixed(1)}
                            <span className="text-xs font-sans text-gray-500 font-normal ml-1" data-id="element-1516">
                              kWh
                            </span>
                          </p>
                        </div>
                      </div>

                      {/* Mini Chart */}
                      <div data-id="element-1517">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2" data-id="element-1518">
                          7-Day Consumption
                        </p>
                        <div style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      height: '64px',
                      gap: '2px'
                    }} data-id="element-1519">

                          {meter.history?.map((val, i) => {
                        const heightPct = maxHistory > 0 ? val / maxHistory * 100 : 0;
                        return <div key={i} style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          flex: 1
                        }} data-id="element-1520">

                                <div style={{
                            width: '100%',
                            height: '48px',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'flex-end',
                            backgroundColor: '#f9fafb'
                          }} data-id="element-1521">

                                  <div style={{
                              width: '100%',
                              height: `${Math.max(heightPct, 2)}%`,
                              backgroundColor: '#0D4F5C',
                              transition: 'all 0.3s',
                              minHeight: '1px'
                            }} data-id="element-1522">
                                  </div>
                                </div>
                                <span style={{
                            fontSize: '9px',
                            color: '#9ca3af',
                            marginTop: '2px',
                            fontWeight: 500
                          }} data-id="element-1523">

                                  {daysOfWeek[i]}
                                </span>
                              </div>;
                      })}
                        </div>
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>;
          }
        })}
        </MapContainer>

        {/* Floating Overlay Stats */}
        <div className="absolute top-4 left-4 right-80 mr-8 z-[1000] pointer-events-none" data-id="element-1524">
          <div className="max-w-7xl mx-auto" data-id="element-1525">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pointer-events-auto" data-id="element-1526">
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md flex items-center gap-3" data-id="element-1527">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-none" data-id="element-1528">
                  <ActivityIcon className="w-4 h-4" data-id="element-1529" />
                </div>
                <div data-id="element-1530">
                  <p className="text-[10px] text-gray-500 font-medium uppercase leading-tight" data-id="element-1531">
                    Total Meters
                  </p>
                  <p className="text-lg font-bold text-gray-900 font-mono leading-tight" data-id="element-1532">
                    {totalMeters.toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md flex items-center gap-3" data-id="element-1533">
                <div className="p-2 bg-mavis-green/10 text-mavis-green rounded-none" data-id="element-1534">
                  <WifiIcon className="w-4 h-4" data-id="element-1535" />
                </div>
                <div data-id="element-1536">
                  <p className="text-[10px] text-gray-500 font-medium uppercase leading-tight" data-id="element-1537">
                    Online Rate
                  </p>
                  <p className="text-lg font-bold text-gray-900 font-mono leading-tight" data-id="element-1538">
                    {onlineRate.toFixed(1)}%
                  </p>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md flex items-center gap-3" data-id="element-1539">
                <div className="p-2 bg-mavis-orange/10 text-mavis-orange rounded-none" data-id="element-1540">
                  <AlertTriangleIcon className="w-4 h-4" data-id="element-1541" />
                </div>
                <div data-id="element-1542">
                  <p className="text-[10px] text-gray-500 font-medium uppercase leading-tight" data-id="element-1543">
                    Active Alerts
                  </p>
                  <p className="text-lg font-bold text-gray-900 font-mono leading-tight" data-id="element-1544">
                    {activeAlerts}
                  </p>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md flex items-center gap-3" data-id="element-1545">
                <div className="p-2 bg-purple-50 text-purple-600 rounded-none" data-id="element-1546">
                  <ZapIcon className="w-4 h-4" data-id="element-1547" />
                </div>
                <div data-id="element-1548">
                  <p className="text-[10px] text-gray-500 font-medium uppercase leading-tight" data-id="element-1549">
                    {avgDailyLabel}
                  </p>
                  <p className="text-lg font-bold text-gray-900 font-mono leading-tight" data-id="element-1550">
                    {avgDaily.toLocaleString(undefined, {
                    maximumFractionDigits: 1
                  })}{' '}
                    <span className="text-[10px] font-sans text-gray-500" data-id="element-1551">
                      {avgDailyUnit}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Legend */}
        <div className="absolute bottom-6 left-4 z-[1000]" data-id="element-1552">
          <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md flex gap-8" data-id="element-1553">
            <div data-id="element-1554">
              <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1" data-id="element-1555">
                Meter Type
              </h4>
              <div className="space-y-2" data-id="element-1556">
                <div className="flex items-center gap-2" data-id="element-1557">
                  <div className="w-3 h-3 rounded-full bg-gray-300 border-2 border-white shadow-sm" data-id="element-1558"></div>
                  <span className="text-xs font-medium text-gray-700" data-id="element-1559">
                    Residential
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1560">
                  <div className="w-4 h-4 rounded-full bg-gray-300 border-[3px] border-white shadow-sm" data-id="element-1561"></div>
                  <span className="text-xs font-medium text-gray-700" data-id="element-1562">
                    Large Power User
                  </span>
                </div>
              </div>
            </div>

            <div data-id="element-1563">
              <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1" data-id="element-1564">
                Status
              </h4>
              <div className="space-y-2" data-id="element-1565">
                <div className="flex items-center gap-2" data-id="element-1566">
                  <div className="w-3 h-3 rounded-full bg-[#8DB43E] border border-white shadow-sm" data-id="element-1567"></div>
                  <span className="text-xs font-medium text-gray-700" data-id="element-1568">
                    Online
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1569">
                  <div className="w-3 h-3 rounded-full bg-[#E07020] border border-white shadow-sm" data-id="element-1570"></div>
                  <span className="text-xs font-medium text-gray-700" data-id="element-1571">
                    Warning
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-1572">
                  <div className="w-3 h-3 rounded-full bg-[#ef4444] border border-white shadow-sm" data-id="element-1573"></div>
                  <span className="text-xs font-medium text-gray-700" data-id="element-1574">
                    Offline
                  </span>
                </div>
              </div>
            </div>

            {viewMode !== 'residential' && <div data-id="element-1575">
                <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1" data-id="element-1576">
                  LPU Type
                </h4>
                <div className="space-y-2" data-id="element-1577">
                  <div className="flex items-center gap-2" data-id="element-1578">
                    <div className="w-3 h-3 rounded-none bg-[#3b82f6] border border-white shadow-sm" data-id="element-1579"></div>
                    <span className="text-xs font-medium text-gray-700" data-id="element-1580">
                      Mall
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-1581">
                    <div className="w-3 h-3 rounded-none bg-[#8b5cf6] border border-white shadow-sm" data-id="element-1582"></div>
                    <span className="text-xs font-medium text-gray-700" data-id="element-1583">
                      Factory
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-1584">
                    <div className="w-3 h-3 rounded-none bg-[#22c55e] border border-white shadow-sm" data-id="element-1585"></div>
                    <span className="text-xs font-medium text-gray-700" data-id="element-1586">
                      School
                    </span>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Floating Meter List Panel */}
      <div className="absolute right-4 top-4 bottom-6 z-[1000] w-80 bg-white/95 backdrop-blur-sm border border-gray-200 shadow-lg rounded-none flex flex-col" data-id="element-1587">
        {/* Panel Header */}
        <div className="p-4 border-b border-gray-200 bg-gray-50/80" data-id="element-1588">
          <div className="flex justify-between items-center mb-3" data-id="element-1589">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-1590">
              <GaugeIcon className="w-5 h-5 text-mavis-teal" data-id="element-1591" />
              Meters
            </h2>
            <span className="px-2 py-0.5 bg-mavis-teal/10 text-mavis-teal text-xs font-bold rounded-none" data-id="element-1592">
              {filteredDataset.length}
            </span>
          </div>
          <div className="relative mb-3" data-id="element-1593">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1594" />
            <input type="text" placeholder="Search meters..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-none text-sm focus:outline-none focus:ring-1 focus:ring-mavis-teal focus:border-mavis-teal transition-colors" data-id="element-1595" />

          </div>

          {/* View Mode Tabs */}
          <div className="flex border border-gray-200 rounded-none overflow-hidden bg-white" data-id="element-1596">
            <button onClick={() => setViewMode('all')} className={`flex-1 py-1.5 text-xs font-medium transition-colors ${viewMode === 'all' ? 'bg-mavis-teal text-white' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-1597">

              All
            </button>
            <button onClick={() => setViewMode('residential')} className={`flex-1 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors ${viewMode === 'residential' ? 'bg-mavis-teal text-white border-l-transparent' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-1598">

              Residential
            </button>
            <button onClick={() => setViewMode('lpu')} className={`flex-1 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors ${viewMode === 'lpu' ? 'bg-mavis-teal text-white border-l-transparent' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-1599">

              LPU
            </button>
          </div>
        </div>

        {/* Meter List */}
        <div className="flex-1 overflow-y-auto" data-id="element-1600">
          {paginatedDataset.length === 0 ? <div className="p-6 text-center text-gray-500 text-sm" data-id="element-1601">
              No meters found matching your search.
            </div> : <div className="divide-y divide-gray-100" data-id="element-1602">
              {paginatedDataset.map(meter => {
            const isSelected = selectedMeterId === meter.id;
            const color = getStatusColor(meter.status);
            return <div key={meter.id} onClick={() => setSelectedMeterId(meter.id)} className={`p-3 cursor-pointer transition-colors flex justify-between items-center ${isSelected ? 'bg-mavis-teal/5 border-l-2 border-l-mavis-teal' : 'hover:bg-gray-50 border-l-2 border-l-transparent'}`} data-id="element-1603">

                    <div className="flex items-center gap-3 overflow-hidden" data-id="element-1604">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{
                  backgroundColor: color
                }} data-id="element-1605" />

                      <div className="truncate" data-id="element-1606">
                        <p className={`text-sm font-mono font-medium truncate ${isSelected ? 'text-mavis-teal font-bold' : 'text-gray-900'}`} data-id="element-1607">

                          {meter.id}
                        </p>
                        <p className="text-xs text-gray-500 truncate" data-id="element-1608">
                          {meter.isLpu ? meter.name : meter.name}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2" data-id="element-1609">
                      <p className="text-sm font-mono font-bold text-gray-900" data-id="element-1610">
                        {meter.isLpu ? meter.consumption?.toLocaleString() : meter.reading?.toFixed(1)}{' '}
                        <span className="text-[10px] font-sans font-normal text-gray-500" data-id="element-1611">
                          kWh
                        </span>
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-1612">
                        {meter.isLpu ? `${meter.peakDemand?.toFixed(1)} MVA` : `${meter.daily?.toFixed(1)} daily`}
                      </p>
                    </div>
                  </div>;
          })}
            </div>}
        </div>

        {/* Pagination Footer */}
        <div className="p-3 border-t border-gray-200 bg-gray-50/80 flex items-center justify-between" data-id="element-1613">
          <span className="text-xs text-gray-500 font-medium" data-id="element-1614">
            {filteredDataset.length > 0 ? <>
                {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, filteredDataset.length)}{' '}
                of {filteredDataset.length}
              </> : '0 meters'}
          </span>
          <div className="flex gap-1" data-id="element-1615">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || filteredDataset.length === 0} className="p-1.5 bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-none transition-colors" data-id="element-1616">

              <ChevronLeftIcon className="w-4 h-4" data-id="element-1617" />
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || filteredDataset.length === 0} className="p-1.5 bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-none transition-colors" data-id="element-1618">

              <ChevronRightIcon className="w-4 h-4" data-id="element-1619" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}